import React, { useCallback, useEffect, useMemo } from "react";
import { Box } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { Route, RouteComponentProps } from "react-router";
import clsx from "clsx";
import { useDeviceTypes } from "halifax";
import { CheckoutStateProvider } from "@capone/checkout";

import { CoreActivitiesCheckout, VIEWED_ACTIVITIES_CHECKOUT } from "redmond";
import { useIsTravelApp } from "@capone/common";
import { useDispatch, useSelector } from "react-redux";
import { ActivityBookConnectorProps } from "./container";
import {
  DesktopActivityBookWorkflow,
  MobileActivityBookWorkflow,
  ConfirmationPage,
  BookingErrorModal,
  BookingInProgressModal,
} from "./components";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_SHOP,
} from "../../utils/paths";
import "./styles.scss";
import { activityBookStateMachine } from "./state/stateMachine";
import { getInitialActivityMachineContext } from "./state/initialContext";
import { Actions } from "./state/actions";
import * as services from "./state/services";
import { Guards } from "./state/guards";
import {
  populateCheckoutQueryParams,
  transitionStateOnPathnameChange,
  validateContext,
} from "./state/utils";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { useExperiments } from "../../context/experiments";
import {
  setExperiencesShopDetails,
  setExperiencesShopOptions,
  setTravelerCounts,
  setSelectedBookableItem,
  setSelectedDate,
  setSelectedTime,
} from "../shop/actions/actions";
import { setRewardsAccounts } from "../rewards/actions/actions";
import {
  mapCoreShopDetails,
  mapCorePriceBreakdowns,
  mapCoreTravelerCounts,
  findCoreSelectedBookableItem,
} from "../../utils/coreDataAdapter";
import { actions as searchActions } from "../search/actions";
import { getEntryType, getEntryTypesAll, getLocation as getSearchLocation } from "../search/reducer";
import { resolveEntryTypesFromQueryString } from "./utils/resolveEntryTypesFromQueryString";
import {
  getLocationNameToResolve,
  fetchLocationForBookPage,
} from "./utils/resolveLocationFromQueryString";

export interface IActivityBookProps
  extends RouteComponentProps,
  ActivityBookConnectorProps {
  core?: CoreActivitiesCheckout;
}

export const ActivityBook = (props: IActivityBookProps) => {
  const {
    location,
    shopDetails,
    priceBreakdown,
    travelerCounts,
    rewardsAccounts,
    shopOptionTrackingProperties,
    activityDetailsProperties,
    opaqueProductToken,
    selectedBookableItem,
    core,
  } = props;

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const history = useHistory();
  const expState = useExperiments();
  const dispatch = useDispatch();

  const coreValues = useMemo(() => {
    if (core == null) return null;

    const mappedShopDetails = mapCoreShopDetails(core);
    const mappedPriceBreakdowns = mapCorePriceBreakdowns(core);
    const mappedTravelerCounts = mapCoreTravelerCounts(core);
    const mappedSelectedItem = findCoreSelectedBookableItem(
      mappedShopDetails.response,
      core.selectedBookableItemId,
    );

    const selectedPriceBreakdown =
      core.selectedStartTime != null
        ? mappedPriceBreakdowns.find(
            (pb) => pb.startTime === core.selectedStartTime,
          ) ?? mappedPriceBreakdowns[0]
        : mappedPriceBreakdowns[0];

    return {
      shopDetails: mappedShopDetails,
      priceBreakdown: selectedPriceBreakdown,
      priceBreakdowns: mappedPriceBreakdowns,
      travelerCounts: mappedTravelerCounts,
      selectedBookableItem: mappedSelectedItem,
      opaqueToken: core.shopOptionSummary.opaqueActivityToken,
    };
  }, [core]);

  useEffect(() => {
    if (core == null || coreValues == null) return;

    dispatch(setExperiencesShopDetails(coreValues.shopDetails));
    dispatch(
      setExperiencesShopOptions(
        coreValues.priceBreakdowns,
        core.shopOptionSummary.trackingProperties,
        coreValues.opaqueToken,
      ),
    );
    dispatch(setTravelerCounts(coreValues.travelerCounts));
    dispatch(setSelectedBookableItem(coreValues.selectedBookableItem));

    if (core.selectedDate != null) {
      dispatch(setSelectedDate(core.selectedDate));
    }
    if (core.selectedStartTime != null) {
      dispatch(setSelectedTime(core.selectedStartTime));
    }

    dispatch(setRewardsAccounts(core.rewardsAccounts));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const existingEntryType = useSelector(getEntryType);
  const existingEntryTypesAll = useSelector(getEntryTypesAll);

  useEffect(() => {
    // Direct Book-page loads (e.g. under the core-activities funnel) never go
    // through the Shop page's fetchExperiencesShopSaga, so entryType/entryTypesAll
    // forwarded on the checkout URL would otherwise never reach Redux.
    const resolved = resolveEntryTypesFromQueryString({
      search: history.location.search,
      existingEntryType,
      existingEntryTypesAll,
    });
    if (!resolved) return;

    dispatch(searchActions.setEntryTypesAll(resolved.entryTypesAll));
    dispatch(searchActions.setEntryType(resolved.entryType));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const existingSearchLocation = useSelector(getSearchLocation);

  useEffect(() => {
    // Mirrors fetchExperiencesAvailabilitySaga's location resolution for direct
    // Book-page loads, so location_searched on checkout tracking events isn't
    // silently empty when Availability never ran this session.
    const locationName = getLocationNameToResolve({
      search: history.location.search,
      existingLocation: existingSearchLocation,
    });
    if (!locationName) return;

    fetchLocationForBookPage(locationName)
      .then((resolvedLocation) => {
        if (resolvedLocation) {
          dispatch(searchActions.setLocation(resolvedLocation));
        }
      })
      .catch(() => {
        // Non-critical — checkout can proceed without a resolved search location.
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!shopDetails && core == null) {
    history.push(`${PATH_SHOP}${history.location.search}`);
  }

  const { isTravelApp } = useIsTravelApp();

  const channel = useMemo(
    () => (matchesDesktop ? "Web" : isTravelApp ? "TravelApp" : "Mobile"),
    [matchesDesktop, isTravelApp],
  );

  useEffect(() => {
    const { properties, encryptedProperties } = activityDetailsProperties;
    trackEvent({
      eventName: VIEWED_ACTIVITIES_CHECKOUT,
      properties,
      encryptedProperties,
    });
  }, []);

  const effectiveShopDetails =
    coreValues != null ? coreValues.shopDetails.response : shopDetails;
  const effectivePriceBreakdown =
    coreValues != null ? coreValues.priceBreakdown : priceBreakdown;
  const effectiveTravelerCounts =
    coreValues != null ? coreValues.travelerCounts : travelerCounts;
  const effectiveRewardsAccounts =
    coreValues != null ? core?.rewardsAccounts : rewardsAccounts;
  const effectiveTrackingProperties =
    coreValues != null
      ? core?.shopOptionSummary.trackingProperties
      : shopOptionTrackingProperties;
  const effectiveOpaqueToken =
    coreValues != null ? coreValues.opaqueToken : opaqueProductToken;
  const effectiveSelectedBookableItem =
    coreValues != null ? coreValues.selectedBookableItem : selectedBookableItem;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial context is captured once at mount; CheckoutStateProvider restarts the state machine when getInitialContext changes, so this must be a stable reference
  const getInitialActivityMachineContextCallBack = useCallback(
    () =>
      getInitialActivityMachineContext(
        effectiveShopDetails,
        effectivePriceBreakdown,
        effectiveTravelerCounts,
        effectiveRewardsAccounts,
        effectiveTrackingProperties,
        activityDetailsProperties,
        channel,
        expState.experiments,
        effectiveOpaqueToken,
        effectiveSelectedBookableItem,
      ),
    [],
  );

  return (
    <CheckoutStateProvider
      stateMachine={activityBookStateMachine}
      actions={Actions}
      guards={Guards}
      context={getInitialActivityMachineContextCallBack()}
      services={services}
      getInitialContext={getInitialActivityMachineContextCallBack}
      validateContext={validateContext as (ctx: unknown) => boolean}
      history={history}
      onStateValueChange={
        matchesMobile && isTravelApp ? populateCheckoutQueryParams : undefined
      }
      onPathnameChange={
        matchesMobile && isTravelApp
          ? transitionStateOnPathnameChange
          : undefined
      }
    >
      <Box
        className={clsx(
          "activity-book-root",
          {
            confirm: { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
          },
          { mobile: matchesMobile },
        )}
      >
        <Route path={PATH_BOOK} exact>
          <Box>
            {matchesDesktop && <DesktopActivityBookWorkflow />}
            {matchesMobile && <MobileActivityBookWorkflow />}
          </Box>
          <BookingErrorModal />
          <BookingInProgressModal />
        </Route>
        <Route path={[PATH_BOOK_CONFIRMATION]}>
          <ConfirmationPage />
        </Route>
      </Box>
    </CheckoutStateProvider>
  );
};
